import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { messagesAPI } from '../lib/api';
import { Send, ChevronLeft, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function timeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function MessagesPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMsg, setSendingMsg] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth'); return; }
    loadConversations();
  }, [user, navigate, authLoading]);

  const loadConversations = async () => {
    try {
      const { data } = await messagesAPI.getConversations();
      setConversations(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConv(conv);
    try {
      const { data } = await messagesAPI.getMessages(conv.conversation_id);
      setMessages(data);
      scrollToBottom();
    } catch (err) {
      console.error(err);
    }

    // Connect WebSocket
    if (wsRef.current) wsRef.current.close();
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    try {
      const ws = new WebSocket(`${wsUrl}/api/ws/chat/${conv.conversation_id}`);
      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        setMessages(prev => {
          if (prev.some(m => m.message_id === msg.message_id)) return prev;
          return [...prev, msg];
        });
        scrollToBottom();
      };
      wsRef.current = ws;
    } catch (err) {
      console.error('WebSocket connection failed, falling back to polling');
    }

    // Poll as fallback
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await messagesAPI.getMessages(conv.conversation_id);
        setMessages(data);
      } catch {}
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const scrollToBottom = () => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleSend = async () => {
    if (!newMsg.trim() || !selectedConv) return;
    setSendingMsg(true);
    const otherUserId = selectedConv.participants?.find(p => p !== user.user_id);
    try {
      // Try WebSocket first
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          sender_id: user.user_id,
          receiver_id: otherUserId,
          content: newMsg.trim()
        }));
      } else {
        await messagesAPI.send({
          receiver_id: otherUserId,
          listing_id: selectedConv.listing_id,
          content: newMsg.trim()
        });
        const { data } = await messagesAPI.getMessages(selectedConv.conversation_id);
        setMessages(data);
      }
      setNewMsg('');
      scrollToBottom();
    } catch (err) {
      console.error(err);
    } finally {
      setSendingMsg(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-white" data-testid="messages-page">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 mb-6">Messages</h1>

        <div className="grid md:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
          {/* Conversations List */}
          <div className={`md:col-span-1 border border-zinc-200 rounded-lg overflow-hidden flex flex-col ${selectedConv ? 'hidden md:flex' : 'flex'}`} data-testid="conversations-list">
            <div className="p-4 border-b border-zinc-200 bg-zinc-50">
              <h2 className="font-bold text-sm text-zinc-700">Conversations</h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-sm text-zinc-400">Loading...</div>
              ) : conversations.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageCircle className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                  <p className="text-sm text-zinc-500">No conversations yet</p>
                  <p className="text-xs text-zinc-400 mt-1">Start by messaging a seller</p>
                </div>
              ) : (
                conversations.map(conv => (
                  <button
                    key={conv.conversation_id}
                    onClick={() => selectConversation(conv)}
                    className={`w-full text-left p-4 border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${selectedConv?.conversation_id === conv.conversation_id ? 'bg-zinc-50' : ''}`}
                    data-testid={`conversation-${conv.conversation_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center text-sm font-bold text-zinc-500 flex-shrink-0 overflow-hidden">
                        {conv.other_user?.avatar ? (
                          <img src={conv.other_user.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : conv.other_user?.name?.charAt(0) || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm text-zinc-900 truncate">{conv.other_user?.name || 'Unknown'}</span>
                          <span className="text-xs text-zinc-400">{timeAgo(conv.last_message_at)}</span>
                        </div>
                        <p className="text-xs text-zinc-500 truncate">{conv.last_message}</p>
                        {conv.listing && (
                          <p className="text-xs text-[#002FA7] truncate mt-0.5">Re: {conv.listing.title}</p>
                        )}
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="w-5 h-5 bg-[#002FA7] text-white rounded-full text-xs flex items-center justify-center font-bold flex-shrink-0">{conv.unread_count}</span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className={`md:col-span-2 border border-zinc-200 rounded-lg overflow-hidden flex flex-col ${!selectedConv ? 'hidden md:flex' : 'flex'}`} data-testid="chat-area">
            {selectedConv ? (
              <>
                <div className="p-4 border-b border-zinc-200 bg-zinc-50 flex items-center gap-3">
                  <button onClick={() => setSelectedConv(null)} className="md:hidden" data-testid="back-to-conversations">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-sm font-bold text-zinc-500">
                    {selectedConv.other_user?.name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-zinc-900">{selectedConv.other_user?.name}</p>
                    {selectedConv.listing && <p className="text-xs text-zinc-500">Re: {selectedConv.listing.title}</p>}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
                  {messages.map(msg => (
                    <div key={msg.message_id} className={`flex ${msg.sender_id === user.user_id ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${msg.sender_id === user.user_id ? 'bg-[#002FA7] text-white rounded-br-md' : 'bg-zinc-100 text-zinc-900 rounded-bl-md'}`}>
                        <p>{msg.content}</p>
                        <p className={`text-[10px] mt-1 ${msg.sender_id === user.user_id ? 'text-blue-200' : 'text-zinc-400'}`}>{timeAgo(msg.created_at)}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                <div className="p-4 border-t border-zinc-200 flex gap-2">
                  <input
                    type="text" value={newMsg} onChange={e => setNewMsg(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSend()}
                    placeholder="Type a message..."
                    className="flex-1 border border-zinc-200 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    data-testid="chat-input"
                  />
                  <button onClick={handleSend} disabled={sendingMsg || !newMsg.trim()}
                    className="w-10 h-10 bg-[#002FA7] text-white rounded-full flex items-center justify-center hover:bg-[#002585] transition-colors disabled:opacity-50"
                    data-testid="chat-send-btn"
                  ><Send className="w-4 h-4" /></button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <MessageCircle className="w-12 h-12 text-zinc-200 mb-4" />
                <p className="text-zinc-500 font-medium">Select a conversation</p>
                <p className="text-sm text-zinc-400 mt-1">Choose a chat to start messaging</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
